import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { BLOG_POSTS } from "@/data/blog";
import { useSEO } from "@/lib/useSEO";
import { SITE_URL as SITE } from "@/lib/site";

const fmt = (d) => new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

export default function Blog() {
  useSEO({
    title: "Insights — Security & Connectivity Blog | Endasphere",
    description:
      "Guides and insights on cybersecurity, anti-cheat, reverse-proxy CDNs, privacy and cloud infrastructure from the Endasphere team.",
    keywords: "cybersecurity blog, anti-cheat, reverse proxy, CDN, privacy, cloud security, network infrastructure insights",
    path: "/blog",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Blog",
      name: "Endasphere Insights",
      url: `${SITE}/blog`,
      blogPost: BLOG_POSTS.map((p) => ({
        "@type": "BlogPosting",
        headline: p.title,
        datePublished: p.date,
        url: `${SITE}/blog/${p.slug}`,
      })),
    },
  });

  return (
    <div data-testid="blog-index">
      <section className="relative pt-[140px] lg:pt-[180px] pb-16 border-b border-white/10">
        <div className="absolute inset-0 bg-grid opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10">
          <span className="text-xs uppercase tracking-[0.3em] text-[#22D3EE]">Insights</span>
          <h1 className="mt-5 font-display font-900 text-5xl sm:text-6xl lg:text-7xl tracking-tighter text-white leading-none">
            The Endasphere<br />blog
          </h1>
          <p className="mt-8 max-w-xl text-base text-slate-400 leading-relaxed">
            Field notes on security, anti-cheat, connectivity and the edge — written for the people building on the sphere.
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {BLOG_POSTS.map((p, i) => (
            <motion.div
              key={p.slug}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.06 }}
            >
              <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group block rounded-2xl border border-white/10 bg-[#0A0A12] overflow-hidden hover:border-[#22D3EE]/40 transition-colors duration-300 h-full">
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-105 transition-all duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A12] to-transparent" />
                  <span className="absolute top-4 left-4 text-[10px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full bg-black/50 backdrop-blur border border-white/10" style={{ color: p.accent }}>
                    {p.serviceNav}
                  </span>
                </div>
                <div className="p-7">
                  <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.15em] text-slate-500">
                    <span>{fmt(p.date)}</span><span className="text-slate-700">·</span><span>{p.readTime}</span>
                  </div>
                  <h2 className="mt-4 font-display font-700 text-xl text-white tracking-tight leading-snug group-hover:text-[#22D3EE] transition-colors duration-300">
                    {p.title}
                  </h2>
                  <p className="mt-3 text-sm text-slate-400 leading-relaxed line-clamp-3">{p.excerpt}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.15em] text-[#22D3EE]">
                    Read <ArrowUpRight className="w-3.5 h-3.5 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-transform" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
